/**
 * TemplateSelector Component
 *
 * A dropdown + save button for template selection on sections.
 * Used for Personal Message, Email Signature, and Subject Line.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Save, Trash2, FileText } from 'lucide-react';
import type { SectionTemplate } from '../types/emailTemplates';
import { TemplateSavePopover } from './TemplateSavePopover';

interface TemplateSelectorProps {
  /** Current templates available */
  templates: SectionTemplate[];
  /** Current value of the field (to save) */
  currentValue: string;
  /** Callback when a template is selected */
  onSelect: (content: string) => void;
  /** Callback to save new template */
  onSave: (name: string, content: string) => Promise<void>;
  /** Callback to delete a template */
  onDelete: (id: string) => Promise<void>;
  /** Placeholder text */
  placeholder?: string;
  /** Compact mode (smaller) */
  compact?: boolean;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  currentValue,
  onSelect,
  onSave,
  onDelete,
  placeholder = 'Choose template...',
  compact = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSavePopoverOpen, setIsSavePopoverOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Handle template selection
  const handleSelect = useCallback((template: SectionTemplate) => {
    onSelect(template.content);
    setIsDropdownOpen(false);
  }, [onSelect]);

  // Handle save
  const handleSave = useCallback(async (name: string) => {
    await onSave(name, currentValue);
  }, [onSave, currentValue]);

  // Handle delete
  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  }, [onDelete]);

  const buttonClasses = compact
    ? 'px-2 py-1 text-[10px]'
    : 'px-2.5 py-1.5 text-xs';

  return (
    <div ref={dropdownRef} className="relative flex items-center gap-1">
      {/* Template Dropdown Button */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`flex items-center gap-1 bg-dark-bg border border-dark-border/30 rounded text-dark-muted hover:text-white hover:border-dark-border/30 transition-all duration-150 ${buttonClasses}`}
      >
        <FileText className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        <span className="hidden sm:inline">{templates.length > 0 ? 'Templates' : 'No templates'}</span>
        <ChevronDown className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${isDropdownOpen ? 'rotate-180' : ''} transition-transform`} />
      </button>

      {/* Save Button */}
      <button
        onClick={() => setIsSavePopoverOpen(true)}
        disabled={!currentValue.trim()}
        className={`flex items-center gap-1 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded hover:bg-purple-500/30 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${buttonClasses}`}
        title="Save as template"
      >
        <Save className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        <span className="hidden sm:inline">Save</span>
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 min-w-[200px] max-w-[280px] max-h-[200px] overflow-hidden">
          {templates.length === 0 ? (
            <div className="p-3 text-xs text-dark-muted text-center">
              No saved templates yet
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[200px]">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => handleSelect(template)}
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-dark-border cursor-pointer group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{template.name}</p>
                    <p className="text-[10px] text-dark-muted truncate">
                      {template.content.replace(/<[^>]*>/g, '').slice(0, 50)}...
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDelete(template.id, e)}
                    disabled={deletingId === template.id}
                    className="p-1 text-dark-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Delete template"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save Popover */}
      <TemplateSavePopover
        isOpen={isSavePopoverOpen}
        onClose={() => setIsSavePopoverOpen(false)}
        onSave={handleSave}
        placeholder="Template name..."
        title="Save Template"
      />
    </div>
  );
};

export default TemplateSelector;
